import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { 
  User, MapPin, Bell, Mail, CreditCard, Calendar,
  CheckCircle, XCircle, AlertTriangle, Clock, X,
  Settings, Shield, History, Smartphone
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfilePage = () => {
  const { t } = useLanguage();
  const { user, isSubscribed, logout } = useAuth();
  const navigate = useNavigate();
  
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  // Use user data directly from context for subscription info
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    console.log('User data in profile:', user);
    console.log('subscribed_at:', user.subscribed_at);
    console.log('created_at:', user.created_at);
    
    // Set subscription info from user data
    if (user.is_subscribed) {
      const subAt = user.subscribed_at || user.created_at;
      console.log('Setting subscribed_at to:', subAt);
      setSubscriptionInfo({
        is_subscribed: true,
        subscribed_at: subAt,
        plan: "Premium",
        price: 7.99
      });
    } else {
      setSubscriptionInfo({
        is_subscribed: false,
        subscribed_at: null,
        plan: null,
        price: null
      });
    }
    
    // Fetch alerts separately
    fetchAlerts();
    setLoading(false);
  }, [user, navigate]);

  const fetchAlerts = async () => {
    try {
      const token = localStorage.getItem('visaalerts_token');
      if (!token) {
        return;
      }
      
      const alertsResponse = await axios.get(`${API}/user/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(alertsResponse.data || []);
    } catch (error) {
      console.log('Alerts fetch failed:', error?.response?.status);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getExpirationDate = () => {
    if (!subscriptionInfo?.subscribed_at) return null;
    const subscribedDate = new Date(subscriptionInfo.subscribed_at);
    const expirationDate = new Date(subscribedDate);
    expirationDate.setMonth(expirationDate.getMonth() + 1);
    return expirationDate;
  };

  const getDaysRemaining = () => {
    const expDate = getExpirationDate();
    if (!expDate) return 0;
    const today = new Date();
    const diffTime = expDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  if (!user) {
    return null;
  }

  const preferences = user.preferences || {};

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="profile-page">
      <Navbar />
      
      {/* Cancel Subscription Popup */}
      {showCancelPopup && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 rounded-t-xl relative">
              <button 
                onClick={() => setShowCancelPopup(false)}
                className="absolute top-3 right-3 text-white/80 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-white font-bold text-lg">{t('cancel_subscription')}</p>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <p className="text-gray-700 text-center mb-4">
                {t('cancel_subscription_text')}
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-blue-800 font-medium text-center">
                  <Mail className="w-5 h-5 inline mr-2" />
                  customer_service@visago-alerts.online
                </p>
              </div>
              
              <p className="text-gray-500 text-sm text-center mb-4">
                {t('cancel_subscription_note')}
              </p>
              
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowCancelPopup(false)}
                >
                  {t('close')}
                </Button>
                <Button 
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    window.location.href = 'mailto:customer_service@visago-alerts.online?subject=Cancel Subscription Request&body=Hello, I would like to cancel my subscription. My email: ' + user.email;
                  }}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {t('send_email')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-bold text-[#0A0A0A]" data-testid="profile-title">
              {t('profile_title')}
            </h1>
            <p className="text-gray-600 mt-1">{t('profile_subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - User Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Personal Information */}
              <Card className="rounded-xl shadow-lg border-0">
                <CardHeader className="border-b border-gray-100">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <User className="w-5 h-5 text-[#FF3B30]" />
                    {t('personal_info')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">{t('name')}</p>
                      <p className="font-semibold text-[#0A0A0A]">{user.name || '-'}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">{t('email')}</p>
                      <p className="font-semibold text-[#0A0A0A]">{user.email}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">{t('residence_country')}</p>
                      <p className="font-semibold text-[#0A0A0A] flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#FF3B30]" />
                        {preferences.residence_country || '-'}
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">{t('member_since')}</p>
                      <p className="font-semibold text-[#0A0A0A]">{formatDate(user.created_at)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Preferences */}
              <Card className="rounded-xl shadow-lg border-0">
                <CardHeader className="border-b border-gray-100">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Settings className="w-5 h-5 text-blue-500" />
                    {t('preferences')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-600 mb-1">{t('notification_type')}</p>
                      <p className="font-semibold text-[#0A0A0A] flex items-center gap-2">
                        {preferences.notification_type === 'email' && <Mail className="w-4 h-4" />}
                        {preferences.notification_type === 'sms' && <Smartphone className="w-4 h-4" />}
                        {preferences.notification_type === 'both' && <Bell className="w-4 h-4" />}
                        {preferences.notification_type === 'email' ? 'Email' : 
                         preferences.notification_type === 'sms' ? 'SMS' : 
                         preferences.notification_type === 'both' ? 'Email & SMS' : '-'}
                      </p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-600 mb-1">{t('visa_center')}</p>
                      <p className="font-semibold text-[#0A0A0A]">{preferences.visa_center || '-'}</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-600 mb-1">{t('destination_country')}</p>
                      <p className="font-semibold text-[#0A0A0A]">{preferences.destination_country || '-'}</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-600 mb-1">{t('phone_number')}</p>
                      <p className="font-semibold text-[#0A0A0A]">{preferences.phone_number || '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Alerts History */}
              <Card className="rounded-xl shadow-lg border-0">
                <CardHeader className="border-b border-gray-100">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <History className="w-5 h-5 text-purple-500" />
                    {t('alerts_history')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-8 h-8 border-4 border-[#FF3B30] border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : alerts.length === 0 ? (
                    <div className="text-center py-8">
                      <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">{t('no_alerts_yet')}</p>
                      <p className="text-sm text-gray-400 mt-1">{t('no_alerts_desc')}</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {alerts.map((alert, index) => (
                        <div 
                          key={index}
                          className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            alert.type === 'email' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
                          }`}>
                            {alert.type === 'email' ? <Mail className="w-5 h-5" /> : <Smartphone className="w-5 h-5" />}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-[#0A0A0A]">
                              {alert.destination || 'France'} {t('visa_appointment')}
                            </p>
                            <p className="text-sm text-gray-500">
                              {alert.visa_center || 'TLS'} - {alert.location || preferences.residence_country}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-400">{formatDate(alert.sent_at)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Subscription */}
            <div className="space-y-6">
              {/* Subscription Status */}
              <Card className={`rounded-xl shadow-lg border-2 ${isSubscribed ? 'border-green-300 bg-green-50/50' : 'border-gray-200'}`}>
                <CardHeader className="border-b border-gray-100">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <CreditCard className="w-5 h-5 text-green-500" />
                    {t('subscription')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {isSubscribed ? (
                    <>
                      <div className="flex items-center gap-2 mb-4">
                        <CheckCircle className="w-6 h-6 text-green-500" />
                        <span className="font-bold text-green-700 text-lg">{t('active_subscription')}</span>
                      </div>
                      
                      <div className="space-y-3 mb-6">
                        <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                          <span className="text-gray-600">{t('plan')}</span>
                          <span className="font-semibold">Premium</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                          <span className="text-gray-600">{t('price')}</span>
                          <span className="font-semibold text-[#FF3B30]">€7.99/{t('month')}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                          <span className="text-gray-600">{t('expires_on')}</span>
                          <span className="font-semibold">{formatDate(getExpirationDate())}</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                          <span className="text-amber-700">{t('days_remaining')}</span>
                          <span className="font-bold text-amber-700">{getDaysRemaining()} {t('days')}</span>
                        </div>
                      </div>
                      
                      <Button 
                        variant="outline"
                        className="w-full border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() => setShowCancelPopup(true)}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        {t('cancel_subscription')}
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-4">
                        <XCircle className="w-6 h-6 text-gray-400" />
                        <span className="font-bold text-gray-600 text-lg">{t('no_subscription')}</span>
                      </div>
                      
                      <p className="text-gray-500 mb-6">{t('no_subscription_desc')}</p>
                      
                      <Button 
                        className="w-full bg-[#FF3B30] hover:bg-[#E5352B] text-white font-bold"
                        onClick={() => navigate('/dashboard')}
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        {t('subscribe_now')} - €7.99/{t('month')}
                      </Button>
                      
                      <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                        <p className="text-amber-700 text-sm text-center flex items-center justify-center gap-2">
                          <Shield className="w-4 h-4" />
                          {t('money_back_guarantee')}
                        </p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card className="rounded-xl shadow-lg border-0">
                <CardHeader className="border-b border-gray-100">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Bell className="w-5 h-5 text-[#FF3B30]" />
                    {t('your_stats')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{t('alerts_received')}</span>
                      <span className="font-bold text-[#FF3B30] text-xl">{alerts.length}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{t('account_status')}</span>
                      <span className={`font-semibold ${isSubscribed ? 'text-green-600' : 'text-gray-400'}`}>
                        {isSubscribed ? t('premium') : t('free')}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ProfilePage;
